import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
} from 'ui-components/services/notification-service';

import { useModal } from '../../../../hooks/use-modal';
import useFetchShipFroms from '../../../../pages/settings/ship-from-locations/use-fetch-ship-froms';
import { trackShipFromConfigStart } from '../../../../ship-from/tracking-events';
import ShipFromModal from '../../../../ship-from/modals';
import { SimpleDropdown } from '../../../simple-dropdown';
import Globals from '../../../../store/global-selectors';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';

import { messages } from './messages';
import storeMessages from '../../../../pages/settings/stores/selected-store/messages';

const CREATE_SHIP_FROM = 'CREATE_SHIP_FROM';
const createNewShipFromItem = {
  key: CREATE_SHIP_FROM,
  name: <FormattedMessage {...messages.createShipFrom} />,
};
export const ShipFromLinkDropdown = ({ onSelectShipFrom, selectedShipFromId = '' }) => {
  const { open, close, isOpen } = useModal();
  const { fetchShipFroms } = useFetchShipFroms();

  const sellerShipFroms = useSelector(Globals.selectSellerShipFroms);
  const shipFromDropdownItems = useMemo(() => {
    const mappedSFs = sellerShipFroms.map((s) => ({
      key: s.shipFromId,
      name: s.name,
    }));
    mappedSFs.push(createNewShipFromItem);
    return mappedSFs;
  }, [sellerShipFroms]);

  const onSelectItem = useCallback(
    (shipFromId) => {
      if (shipFromId === CREATE_SHIP_FROM) {
        open();
        trackShipFromConfigStart(EventSourceStrings.RATE_BROWSER_MISSING_SHIP_FROM);
        return;
      }
      onSelectShipFrom({ shipFromId });
    },
    [onSelectShipFrom, open],
  );

  const onSuccess = useCallback(
    async (shipFrom) => {
      try {
        await fetchShipFroms();
        close();
        onSelectShipFrom(shipFrom);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage {...storeMessages.addShipFromSuccessMessageTitle} />,
          content: '',
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...storeMessages.addShipFromErrorMessageTitle} />,
          content: '',
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      }
    },
    [fetchShipFroms, close, onSelectShipFrom],
  );

  return (
    <>
      <SimpleDropdown
        defaultTitle={<FormattedMessage {...messages.shipFromDropdownDefaultText} />}
        items={shipFromDropdownItems}
        onItemSelected={onSelectItem}
        selectedItemKey={selectedShipFromId}
        theme='link'
        variant='button'
      />
      {isOpen && <ShipFromModal onClose={close} onSuccess={onSuccess} source={EventSourceStrings.RATE_BROWSER_MISSING_SHIP_FROM} />}
    </>
  );
};

ShipFromLinkDropdown.propTypes = {
  onSelectShipFrom: PropTypes.func.isRequired,
  selectedShipFromId: PropTypes.string,
};
